import { View, Text, StyleSheet } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { Colors, SELECT_STYLES } from "@/constants";
import PrimaryButton from "./PrimaryButton";
import { useState } from "react";
import useFileSelector from "@/models/useFileSelector";
import toast from "react-hot-toast";
import { useUserSlice } from "@/models/useUserSlice";
import CheckboxButton from "./CheckboxButton";
import { useRouter } from "@/_router";
import { getBasePath } from "@/models/utils";

const AlertWidgetCreateNotePDForImageImport = () => {
  const visible = useUISlice((state) => state.alertWidget === "pdf_import");
  const [OCRSelected, setOCRSelected] = useState(false);
  const setVisible = useUISlice((state) => state.setAlertWidget);
  const { openFilePicker } = useFileSelector();
  const router = useRouter();
  const width = useUISlice((state) => state.device.width);
  const { t } = useTranslation();

  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const folders = useUserSlice((state) => {
    if (state?.folders?.length === 0) return undefined;
    return [
      {
        id: undefined,
        name: t("alertWidgetCreateNotePDForImageImport.allNotes"),
      },
      ...state.folders,
    ];
  });

  if (!visible) return null;

  const onPress = async () => {
    const supportedFileTypes = [".pdf", ".png", ".jpg", ".jpeg"];
    const file = await openFilePicker(supportedFileTypes);

    if (!file) return;

    const fileExtension = file?.name?.split(".").pop();

    if (!supportedFileTypes.includes(`.${fileExtension}`)) {
      toast.error(
        t("alertWidgetCreateNotePDForImageImport.onlyPDFAndImageFilesSupported")
      );
      _onDismiss();
      return;
    }

    const enableOCR = OCRSelected || fileExtension !== "pdf";

    _onDismiss();
    useUserSlice
      .getState()
      .note()
      .create(
        {
          type: enableOCR ? "ocr_text" : "pdf_import",
          name: file.name,
          status: "upload_started",
        },
        folderId,
        (note) => {
          useUISlice.getState().setNoteScreenData({ upload_started: { file } });

          setVisible(undefined);

          router?.push(`${getBasePath()}/note?id=${note.id}`);
        }
      );
  };

  const _onDismiss = () => {
    setOCRSelected(false);
    setVisible(undefined);
  };

  return (
    <ModalContainer
      title={t("alertWidgetCreateNotePDForImageImport.title")}
      onDismiss={_onDismiss}
      hideCancel
      modalWidth={Math.min(width, 600)}
    >
      <View style={styles.spacer} />

      {folders && (
        <>
          <View style={styles.spacer} />
          <Text style={styles.label}>
            {t("alertWidgetCreateNotePDForImageImport.folder")}
          </Text>
          <select
            style={SELECT_STYLES}
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
          >
            {folders.map((folder, index) => (
              <option key={index} value={folder.id}>
                {`📂 ${folder.name}`}
              </option>
            ))}
          </select>
        </>
      )}
      <View style={styles.spacer} />

      <CheckboxButton
        title={t("alertWidgetCreateNotePDForImageImport.ocrRecognizeText")}
        onCheckboxChange={() => setOCRSelected(!OCRSelected)}
        value={OCRSelected}
      />

      <View style={styles.largerSpacer} />

      <PrimaryButton
        icon="upload"
        text={t("alertWidgetCreateNotePDForImageImport.button")}
        onPress={onPress}
      />
      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(16),
  },
  largerSpacer: {
    height: ms(48),
  },
  label: {
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
    marginBottom: ms(8),
  },
  negativeMargin: {
    marginBottom: -ms(16),
  },
});

export default AlertWidgetCreateNotePDForImageImport;
